import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  FileImage,
  Handshake,
  Pause,
  PencilLine,
  PhoneCall,
  Play,
  Presentation,
  Upload,
  Users
} from "lucide-react";
import { formatCurrency, formatDate } from "../../format";
import type { RealEstateCase } from "../../types";
import { useMarketAnalysisContext } from "../market/sync/useMarketAnalysisSync";
import { buildClientMarketReport, buildSalesSummary } from "./reports";
import { safelyDerivedMarketTimeline } from "./domain";
import { unavailableScreenshotParser } from "./providers";
import type { MarketStatsField, MarketStatsInput, MarketStatsSource, MarketTimelineEvent } from "./types";
import "./marketTimeline.css";

const sourceOptions: Array<{ value: MarketStatsSource; label: string }> = [
  { value: "nehutelnosti_sk", label: "Nehnuteľnosti.sk" },
  { value: "bazos_sk", label: "Bazoš.sk" },
  { value: "manual", label: "Manuálne" },
  { value: "screenshot", label: "Screenshot" },
  { value: "other", label: "Iný zdroj" }
];
const sourceLabel = (source?: MarketStatsSource) => sourceOptions.find((option) => option.value === source)?.label ?? "Zdroj neuvedený";

const fields: Array<{ key: MarketStatsField; label: string }> = [
  { key: "views", label: "Zobrazenia" },
  { key: "detailViews", label: "Otvorenia detailu" },
  { key: "calls", label: "Telefonáty" },
  { key: "emails", label: "E-maily" },
  { key: "saved", label: "Uloženia" },
  { key: "viewings", label: "Obhliadky" }
];

const localDateTimeValue = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

type StatsDraft = Record<MarketStatsField, string> & {
  capturedAt: string;
  source: MarketStatsSource;
  notes: string;
  screenshot?: MarketStatsInput["screenshot"];
};

const emptyStatsDraft = (): StatsDraft => ({
  capturedAt: localDateTimeValue(),
  source: "manual",
  views: "",
  detailViews: "",
  calls: "",
  emails: "",
  saved: "",
  viewings: "",
  notes: ""
});

const eventIcon = (event: MarketTimelineEvent) => {
  if (event.type === "LISTED") return Building2;
  if (event.type === "MARKET_STATS_CAPTURED" || event.type === "MARKET_REPORT_CREATED") return BarChart3;
  if (event.type === "PRICE_CHANGED" || event.type === "MARKET_POSITION_CHANGED") return CircleDollarSign;
  if (event.type === "FIRST_CALL") return PhoneCall;
  if (event.type === "FIRST_VIEWING" || event.type === "VIEWING") return Users;
  if (event.type === "RESERVED" || event.type === "BUYER_OFFER") return Handshake;
  if (event.type === "SOLD") return CheckCircle2;
  return PencilLine;
};

const eventValue = (event: MarketTimelineEvent) => {
  if (event.type === "LISTED" && event.metadata?.askingPrice) return formatCurrency(event.metadata.askingPrice);
  if (event.type === "PRICE_CHANGED" && event.metadata?.oldPrice && event.metadata.newPrice) {
    return `${formatCurrency(event.metadata.oldPrice)} → ${formatCurrency(event.metadata.newPrice)}`;
  }
  if (event.type === "SOLD" && event.metadata?.realizedPrice) return formatCurrency(event.metadata.realizedPrice);
  return event.description;
};

type MarketTimelinePanelProps = {
  caseItem: RealEstateCase;
  onSaveStats: (caseId: string, input: MarketStatsInput) => void;
};

export function MarketTimelinePanel({ caseItem, onSaveStats }: MarketTimelinePanelProps) {
  const { analyses } = useMarketAnalysisContext();
  const derivedTimeline = useMemo(() => safelyDerivedMarketTimeline(caseItem), [caseItem]);
  const events = useMemo(
    () => [...derivedTimeline.events].sort((first, second) => Date.parse(first.occurredAt) - Date.parse(second.occurredAt)),
    [derivedTimeline.events]
  );
  const snapshots = useMemo(
    () => [...derivedTimeline.statsSnapshots].sort((first, second) => first.dayFromListing - second.dayFromListing),
    [derivedTimeline.statsSnapshots]
  );
  const availableDays = [...new Set(snapshots.filter((snapshot) => snapshot.dayFromListing === 3 || snapshot.dayFromListing === 7).map((snapshot) => snapshot.dayFromListing as 3 | 7))];
  const [reportDay, setReportDay] = useState<3 | 7 | undefined>(() => availableDays[availableDays.length - 1]);
  const [showEntry, setShowEntry] = useState(false);
  const [clientMode, setClientMode] = useState(false);
  const [draft, setDraft] = useState<StatsDraft>(emptyStatsDraft);
  const [importWarning, setImportWarning] = useState("");
  const [playbackIndex, setPlaybackIndex] = useState<number | null>(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const report = useMemo(() => buildClientMarketReport(caseItem, analyses, reportDay), [analyses, caseItem, reportDay]);
  const salesSummary = useMemo(() => buildSalesSummary(caseItem), [caseItem]);

  useEffect(() => {
    if (availableDays.length > 0 && (!reportDay || !availableDays.includes(reportDay))) {
      setReportDay(availableDays[availableDays.length - 1]);
    }
  }, [availableDays.join("|"), reportDay]);

  useEffect(() => {
    if (!autoPlay || playbackIndex === null || events.length < 2) return;
    const timer = window.setInterval(() => {
      setPlaybackIndex((current) => {
        if (current === null || current >= events.length - 1) {
          setAutoPlay(false);
          return events.length - 1;
        }
        return current + 1;
      });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [autoPlay, events.length, playbackIndex]);

  if (!caseItem.listingStartedAt && events.length === 0) return null;

  const submitStats = (event: FormEvent) => {
    event.preventDefault();
    const input: MarketStatsInput = {
      capturedAt: new Date(draft.capturedAt).toISOString(),
      source: draft.source,
      notes: draft.notes,
      screenshot: draft.screenshot
    };
    fields.forEach(({ key }) => {
      if (draft[key] !== "") input[key] = Math.max(0, Number(draft[key]) || 0);
    });
    onSaveStats(caseItem.id, input);
    setDraft(emptyStatsDraft());
    setShowEntry(false);
    setImportWarning("");
  };

  const currentEvent = playbackIndex === null ? undefined : events[playbackIndex];

  return (
    <section className={`market-timeline-panel ${clientMode ? "client-mode" : ""}`}>
      <header className="market-timeline-header">
        <div>
          <span>FISPRO MARKET</span>
          <h2>Priebeh predaja</h2>
        </div>
        <button className="market-icon-button" type="button" onClick={() => setClientMode((value) => !value)} aria-label={clientMode ? "Zavrieť klientsky režim" : "Otvoriť klientsky režim"}>
          <Presentation size={18} aria-hidden="true" />
        </button>
      </header>

      {report.stats ? (
        <div className="market-hard-data">
          <div><span>Zobrazenia</span><strong>{report.stats.views?.toLocaleString("sk-SK") ?? "–"}</strong>{report.delta?.views !== undefined ? <small>{report.delta.views >= 0 ? "+" : ""}{report.delta.views.toLocaleString("sk-SK")}</small> : null}<em>{sourceLabel(report.stats.fieldSources.views)}</em></div>
          <div><span>Telefonáty</span><strong>{report.stats.calls ?? "–"}</strong>{report.delta?.calls !== undefined ? <small>{report.delta.calls >= 0 ? "+" : ""}{report.delta.calls}</small> : null}<em>{sourceLabel(report.stats.fieldSources.calls)}</em></div>
          <div><span>Obhliadky</span><strong>{report.stats.viewings ?? "–"}</strong>{report.delta?.viewings !== undefined ? <small>{report.delta.viewings >= 0 ? "+" : ""}{report.delta.viewings}</small> : null}<em>{sourceLabel(report.stats.fieldSources.viewings)}</em></div>
        </div>
      ) : null}

      {availableDays.length > 0 ? (
        <div className="market-report-tabs" aria-label="Market report obdobie">
          {availableDays.map((day) => <button key={day} type="button" className={reportDay === day ? "active" : ""} onClick={() => setReportDay(day)}>Deň {day}</button>)}
        </div>
      ) : null}

      {report.marketRange ? (
        <div className="market-position-strip">
          <div><span>Trhové pásmo</span><strong>{formatCurrency(report.marketRange.low)} – {formatCurrency(report.marketRange.high)}</strong></div>
          <div><span>Aktuálna cena</span><strong>{report.currentAskingPrice ? formatCurrency(report.currentAskingPrice) : "–"}</strong></div>
          {report.pricePosition && report.pricePosition.amountAboveHigh > 0 ? <p>+{formatCurrency(report.pricePosition.amountAboveHigh)} / +{report.pricePosition.percentAboveHigh.toLocaleString("sk-SK")} % nad hornou hranicou</p> : null}
        </div>
      ) : null}

      <div className={`market-response-strip response-${report.marketResponse.status.toLowerCase()}`}>
        <span>Market response</span>
        <strong>{report.marketResponse.status === "LOW_RESPONSE" ? "Slabá odozva" : report.marketResponse.status === "NORMAL_RESPONSE" ? "Bežná odozva" : report.marketResponse.status === "STRONG_RESPONSE" ? "Silná odozva" : "Bez benchmarku"}</strong>
        <p>{report.marketResponse.explanation}</p>
      </div>

      {report.topComparables.length > 0 ? (
        <div className="market-report-comparables">
          <span>TOP porovnania</span>
          {report.topComparables.map((comparable) => (
            <div key={comparable.id}><p>{comparable.label}</p><strong>{comparable.realizedPrice ? formatCurrency(comparable.realizedPrice) : comparable.askingPrice ? formatCurrency(comparable.askingPrice) : "–"}</strong><small>{Math.round(comparable.similarityScore)} % zhoda</small></div>
          ))}
        </div>
      ) : null}

      <div className="client-market-timeline">
        {events.length > 0 ? events.map((event) => {
          const Icon = eventIcon(event);
          return (
            <article key={event.id}>
              <div className="client-event-icon"><Icon size={17} aria-hidden="true" /></div>
              <div><time>{formatDate(event.occurredAt.slice(0, 10))}</time><h3>{event.title}</h3>{eventValue(event) ? <p>{eventValue(event)}</p> : null}<small>{event.source === "crm" ? "CRM DATA" : event.source === "market" ? "MARKET DATA" : event.source === "portal" ? "PORTAL DATA" : event.source === "manual" ? "MANUAL DATA" : "FISPRO WORKFLOW"}</small></div>
            </article>
          );
        }) : <p className="market-empty-copy">Priebeh predaja sa začne prvým overeným míľnikom.</p>}
      </div>

      {report.nextRecommendedReview ? (
        <div className="market-next-review"><span>Ďalšie vyhodnotenie</span><strong>{formatDate(report.nextRecommendedReview.date)} · {report.nextRecommendedReview.time}</strong><p>{report.nextRecommendedReview.purpose}</p></div>
      ) : null}

      {caseItem.stage === "Predané" ? (
        <div className="sales-summary">
          <div><span>Čas na trhu</span><strong>{salesSummary.daysOnMarket} dní</strong></div>
          <div><span>Telefonáty</span><strong>{salesSummary.totalCalls}</strong></div>
          <div><span>Obhliadky</span><strong>{salesSummary.totalViewings}</strong></div>
          <div><span>Zmeny ceny</span><strong>{salesSummary.priceChangeCount}×</strong></div>
          <div className="sales-summary-price"><span>Realizovaná cena</span><strong>{salesSummary.realizedPrice ? formatCurrency(salesSummary.realizedPrice) : "–"}</strong>{salesSummary.realizedVsInitialPercent !== undefined ? <small>{salesSummary.realizedVsInitialPercent.toLocaleString("sk-SK")} % z pôvodnej inzertnej ceny</small> : null}</div>
        </div>
      ) : null}

      {!clientMode ? (
        <div className="market-timeline-actions">
          <button className="secondary-button" type="button" onClick={() => setShowEntry((value) => !value)}><BarChart3 size={17} /> Import štatistík</button>
          {events.length > 0 ? <button className="secondary-button" type="button" onClick={() => { setPlaybackIndex(0); setAutoPlay(false); }}><Play size={17} /> Prehrať predaj</button> : null}
        </div>
      ) : events.length > 0 ? <button className="primary-button full-button" type="button" onClick={() => { setPlaybackIndex(0); setAutoPlay(true); }}><Play size={17} /> Prehrať predaj</button> : null}

      {showEntry && !clientMode ? (
        <form className="market-stats-entry" onSubmit={submitStats}>
          <div className="market-stats-entry-top">
            <label><span>Zdroj</span><select value={draft.source} onChange={(event) => setDraft({ ...draft, source: event.target.value as MarketStatsSource })}>{sourceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <label><span>Zachytené</span><input type="datetime-local" value={draft.capturedAt} onChange={(event) => setDraft({ ...draft, capturedAt: event.target.value })} required /></label>
          </div>
          <div className="market-stats-fields">{fields.map(({ key, label }) => <label key={key}><span>{label}</span><input type="number" min="0" inputMode="numeric" value={draft[key]} onChange={(event) => setDraft({ ...draft, [key]: event.target.value })} /></label>)}</div>
          <label className="market-screenshot-upload"><FileImage size={18} /><span>{draft.screenshot?.fileName ?? "Nahrať screenshot"}</span><input type="file" accept="image/*" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; const parsed = await unavailableScreenshotParser.parseScreenshot(file, draft.source); setDraft({ ...draft, source: parsed.detectedSource ?? "screenshot", screenshot: { fileName: file.name, mimeType: file.type, size: file.size } }); setImportWarning(parsed.warnings.join(" ")); }} /></label>
          {importWarning ? <p className="market-import-warning">{importWarning}</p> : null}
          <label className="market-stats-notes"><span>Poznámka</span><input value={draft.notes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} /></label>
          <button className="primary-button full-button" type="submit"><Upload size={17} /> Uložiť historický snapshot</button>
        </form>
      ) : null}

      {currentEvent ? (
        <div className="market-playback-backdrop" role="presentation">
          <section className="market-playback" role="dialog" aria-modal="true" aria-label="Prehrať priebeh predaja">
            <button className="market-playback-close" type="button" onClick={() => { setPlaybackIndex(null); setAutoPlay(false); }}>Zavrieť</button>
            <span>{playbackIndex! + 1} / {events.length}</span>
            <div className="market-playback-event">{(() => { const Icon = eventIcon(currentEvent); return <Icon size={28} />; })()}<time>{formatDate(currentEvent.occurredAt.slice(0, 10))}</time><h2>{currentEvent.title}</h2>{eventValue(currentEvent) ? <p>{eventValue(currentEvent)}</p> : null}</div>
            <div className="market-playback-controls">
              <button type="button" disabled={playbackIndex === 0} onClick={() => setPlaybackIndex((value) => Math.max(0, (value ?? 0) - 1))}><ChevronLeft size={20} /></button>
              <button type="button" onClick={() => setAutoPlay((value) => !value)}>{autoPlay ? <Pause size={20} /> : <Play size={20} />}</button>
              <button type="button" disabled={playbackIndex === events.length - 1} onClick={() => setPlaybackIndex((value) => Math.min(events.length - 1, (value ?? 0) + 1))}><ChevronRight size={20} /></button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
